import { BackgroundProperties } from "../../types/mortgage";

interface InfoIconProps {
  tooltip: string;
}

function InfoIcon({ tooltip }: InfoIconProps) {
  return (
    <div className="relative group inline-block ml-2">
      <svg 
        className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" 
        viewBox="0 0 20 20" 
        fill="currentColor"
      >
        <path 
          fillRule="evenodd" 
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
          clipRule="evenodd" 
        />
      </svg>
      
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-80 text-left">
        <div className="whitespace-normal">
          {tooltip}
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
      </div>
    </div>
  );
}

interface CurrencyInputWithInfoProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  placeholder: string;
  tooltip: string;
}

function CurrencyInputWithInfo({ label, value, onChange, placeholder, tooltip }: CurrencyInputWithInfoProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <label className="block text-sm font-subheader text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <InfoIcon tooltip={tooltip} />
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-500 dark:text-gray-400 sm:text-sm">£</span>
        </div>
        <input
          type="text"
          value={value === 0 ? "" : (value / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          onChange={(e) => {
            const numericValue = e.target.value.replace(/[^\d.]/g, "");
            const parsedValue = parseFloat(numericValue) || 0;
            onChange(Math.round(parsedValue * 100));
          }}
          placeholder={placeholder}
          className="block w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
        />
      </div>
    </div>
  );
}

interface BackgroundBorrowingFormProps {
  background: BackgroundProperties;
  onBackgroundChange: (background: BackgroundProperties) => void;
}

export function BackgroundBorrowingForm({ 
  background, 
  onBackgroundChange
}: BackgroundBorrowingFormProps) {
  const updateBackgroundField = <K extends keyof BackgroundProperties>(
    field: K, 
    value: BackgroundProperties[K]
  ) => {
    onBackgroundChange({ ...background, [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Buy to Let Mortgages */}
      <div>
        <h3 className="text-lg font-subheader text-gray-600 dark:text-gray-300 mb-4">Buy to Let Mortgages</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div className="flex flex-col">
            <CurrencyInputWithInfo
              label="Total value of borrowing"
              value={background.btlTotalValueOfBorrowing}
              onChange={(value) => updateBackgroundField("btlTotalValueOfBorrowing", value)}
              placeholder="0"
              tooltip="Add the amount of borrowing the client has on any BTL or Holiday Let property."
            />
          </div>
        </div>
      </div>

      {/* Other Background Properties */}
      <div>
        <h3 className="text-lg font-subheader text-gray-600 dark:text-gray-300 mb-4">Other Background Properties</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div className="flex flex-col">
            <CurrencyInputWithInfo
              label="Total value of borrowing"
              value={background.otherTotalValueOfBorrowing}
              onChange={(value) => updateBackgroundField("otherTotalValueOfBorrowing", value)}
              placeholder="0"
              tooltip="Add the total amount of borrowing the client has on any other property not covered above (e.g. second properties not rented out)."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
